import { Layout, Table, Typography } from "antd";
import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { formatDataGeneral, getFileLinkIfExist, isDateInSelectedYear } from "utils/utils";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";
import { documentsColumn } from "utils/tableColums";
import { buildSearchableColumns, getDpatTablePagination } from "utils/dpatSearchableTable";

const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";
const O_AND_M_DOC_TYPE = "Operations and Maintenance Plan";
const FINANCIAL_REPORTS_DOC_TYPE = "Financial Reports";

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function buildDocumentHubLink(documents, meType) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const matchedDocs = formatDataGeneral(documentData, "DPAT | M&E Tool Type", meType) || [];

  if (matchedDocs.length === 0) {
    return "Not Uploaded";
  }

  const eventId = getFileLinkIfExist(
    documentReports,
    DOCUMENT_HUB_FILE_ELEMENT,
    matchedDocs[0]?.trackedEntity,
  );

  return eventId
    ? fileUrl(eventId, DOCUMENT_HUB_FILE_ELEMENT)
    : "Not Uploaded";
}

function buildMaintenanceInfrastructureDocLinks(documents) {
  return [
    {
      title: FINANCIAL_REPORTS_DOC_TYPE,
      link: buildDocumentHubLink(documents, FINANCIAL_REPORTS_DOC_TYPE),
    },
    {
      title: "Operation and Maintenance Report",
      link: buildDocumentHubLink(documents, O_AND_M_DOC_TYPE),
    },
  ];
}

const MaintenanceInfrastructure = forwardRef(({
  year,
  buildingInspectorateData,
  documents,
  districtId,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
}, ref) => {
  const { Content } = Layout;
  const { Title } = Typography;
  const [maxScore, setMaxScore] = useState(5);

  const resolvedYear = useMemo(() => {
    if (year == null || year === "") return null;
    const parsed = Number(year);
    return Number.isFinite(parsed) ? parsed : year;
  }, [year]);

  const resolvedBuildingData = useMemo(() => {
    const source = !isAssessmentStarted || !savedAssessmentData
      ? buildingInspectorateData
      : savedAssessmentData.buildingInspectorateData ?? buildingInspectorateData;

    if (!source?.length) {
      return source ?? [];
    }

    if (resolvedYear == null) {
      return source;
    }

    return source.filter((row) => isDateInSelectedYear(row.date, resolvedYear));
  }, [isAssessmentStarted, savedAssessmentData, buildingInspectorateData, resolvedYear]);

  const liveDocumentLinks = useMemo(
    () => buildMaintenanceInfrastructureDocLinks(documents),
    [documents],
  );

  const displayDocumentLinks = useMemo(() => {
    if (isAssessmentStarted && savedAssessmentData?.links) {
      return savedAssessmentData.links;
    }
    return liveDocumentLinks;
  }, [isAssessmentStarted, savedAssessmentData, liveDocumentLinks]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI2",
      area: "Basic/ Social Services",
      maxScore,
      buildingInspectorateData: resolvedBuildingData,
      links: displayDocumentLinks,
    }),
  }));

  const DPAT_SLUG = "sdi2.0-2.4";

  const buildingInspectorateColumn = useMemo(
    () => buildSearchableColumns(
      [
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Supervisor", dataIndex: "supervisor", key: "supervisor" },
        { title: "address", dataIndex: "address", key: "address" },
        { title: "Category of staff", dataIndex: "category", key: "category" },
        { title: "Function performed by Works Department", dataIndex: "department", key: "department" },
      ],
      resolvedBuildingData ?? [],
    ),
    [resolvedBuildingData],
  );

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
          ...col,
          render: (value) => renderDpatEvidenceLink(value, "View Document"),
        }
        : col
    ),
    [],
  );

  return (
    <Comment
      data={resolvedBuildingData}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi2.0-2.4-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 2.0-2.4 Actual Score" score={savedAssessmentData?.score ?? 0} />
          </IndicatorScoreSection>
          <Title level={5} style={{ marginTop: "30px" }}>I. Evidence of O&M Plan in existence</Title>
          <ActualScoreDisplay slug={DPAT_SLUG} label="Actual Score" score={!true ? '1' : '0'} />

          <Title level={5} style={{ marginTop: "30px" }}>II. O&M Budget to Capital Budget</Title>
          <ActualScoreDisplay slug={DPAT_SLUG} label="Actual Score" score={!true ? '1' : '0'} />

          <Title level={5} style={{ marginTop: "30px" }}>III. Evidence of implementation of O&M Plan</Title>
          <ActualScoreDisplay slug={DPAT_SLUG} label="Actual Score" score={!true ? '1' : '0'} />

          <Title level={5} style={{ marginTop: "20px" }}>Evidence (Document Hub)</Title>
          <Table
            columns={documentTableColumns}
            dataSource={displayDocumentLinks}
            rowKey="title"
            pagination={false}
            bordered
          />

          {resolvedBuildingData?.length > 0 && (
            <Table
              columns={buildingInspectorateColumn}
              dataSource={resolvedBuildingData}
              rowKey="key"
              pagination={getDpatTablePagination("records")}
              bordered
              style={{ marginTop: "20px" }}
            />
          )}

          <Content>
            Calculated as: % of IGF i=on Sanitation = (B/A) x 100
          </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default MaintenanceInfrastructure;